import { ReactNode, useRef } from "react";

type SectionHeaderProps = {
  title: string;
  children?: ReactNode;
  /** Variant for section header styling */
  variant?: "large" | "small" | "control";
  /** If provided, makes the title editable */
  onTitleChange?: (newTitle: string) => void;
  /** Optional prefix (e.g., CC number) that's always shown */
  prefix?: string;
};

/**
 * Section header component with optional action buttons.
 * Used throughout the app for consistent section styling.
 *
 * Variants:
 * - large: Main page sections with h2 and text-2xl (Root Note, Scale, etc.)
 * - small: Subsections with h3 and uppercase styling (Range, Spread, etc.)
 * - control: Full-width bordered box with background tint for control headers
 */
export default function SectionHeader({
  title,
  children,
  variant = "large",
  onTitleChange,
  prefix,
}: SectionHeaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Select all text when input is focused
    e.target.select();
  };

  const handlePrefixClick = () => {
    // Focus the input when prefix is clicked
    inputRef.current?.focus();
  };

  if (variant === "large") {
    return (
      <div className="w-full flex items-center justify-between mt-8 mb-4">
        <h2 className="text-2xl">{title}</h2>
        {children}
      </div>
    );
  }

  if (variant === "control") {
    const hasLabel = title.length > 0;

    return (
      <div className="w-full mt-12 mb-3 first:mt-0 flex items-center justify-between py-2 border-b-2 border-gray-700 bg-gradient-to-t from-gray-900/30 to-gray-900/0">
        <div className="flex items-baseline gap-2 flex-1 min-w-0">
          {prefix && (
            <button
              type="button"
              onClick={handlePrefixClick}
              className={`font-medium tracking-wider cursor-text transition-all duration-200 bg-transparent border-none p-0 m-0 focus:outline-none focus:ring-2 focus:ring-purple-500 rounded ${
                hasLabel
                  ? "text-base sm:text-lg text-gray-500"
                  : "text-xl sm:text-2xl text-gray-300"
              }`}
              tabIndex={0}
              aria-label={`Focus input for ${prefix}`}
            >
              {prefix}
            </button>
          )}
          {onTitleChange ? (
            <input
              ref={inputRef}
              type="text"
              value={title}
              onChange={(e) => onTitleChange(e.target.value)}
              onFocus={handleFocus}
              className="text-xl sm:text-2xl font-medium text-gray-300 tracking-wider bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:text-white flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap rounded px-1 -mx-1"
              aria-label={`Edit ${prefix || 'control'} label`}
            />
          ) : (
            <h3 className="text-xl sm:text-2xl font-medium text-gray-300 tracking-wider">
              {title}
            </h3>
          )}
        </div>
        {children}
      </div>
    );
  }

  return (
    <div className="section-yomidi-header">
      <h3 className="section-yomidi-title">{title}</h3>
      {children}
    </div>
  );
}
